import { NavigateFunction } from "react-router-dom";

import {
  ApiService,
  Case,
  CaseDiagnosisAddonWritable,
  CaseDiagnosis,
  CaseReport,
  User,
  UserSupportDocument,
  Patient,
} from "pwapi";

// import { CaseDiagnosis } from "custom-models";

import {
  CASE_DETAIL_REQUEST,
  WIPE_CASE_DETAIL,
  CASE_DETAIL_EVENTS_REQUEST,
  CASE_PATCH_REQUEST,
  CASE_REQUEST_SCREEN_REQUEST,
  CASE_OPEN_DOCUMENT_REQUEST,
  CASE_DIAGNOSES_REQUEST,
  DIAGNOSE_REQUEST,
  CASE_SIGN_OUT_REQUEST,
  UPDATE_DIAGNOSIS_REQUEST,
  STAIN_ORDER_REQUEST,
  CASE_PATCH_STATUS_REQUEST,
  UPDATE_CASE_ADDON_REQUEST,
  CANCEL_EDIT_SCREEN_DIAGNOSES_REQUEST,
  EDIT_SCREEN_DIAGNOSES_REQUEST,
  CASE_RECALL_REPORT_REQUEST,
  CASE_PATIENT_MATCHES_REQUEST,
  CASE_PATIENT_MATCHES_SUCCESS,
  CASE_PATIENT_MATCHES_FAILURE,
  MODIFY_REPORT_REQUEST,
  CANCEL_MODIFY_REPORT_REQUEST,
  REPORT_SIGNEE_FAILURE,
  REPORT_SIGNEE_REQUEST,
  REPORT_SIGNEE_SUCCESS,
  UPDATE_CASE_SPECIMEN_REQUEST,
  UPDATE_CASE_SPECIMEN_SUCCESS,
  UPDATE_CASE_SPECIMEN_FAILURE,
  STAIN_ORDER_SUCCESS,
  STAIN_ORDER_FAILURE,
  CASE_OPEN_SUPPORT_DOCUMENT_REQUEST,
  CASE_OPEN_SUPPORT_DOCUMENT_SUCCESS,
  CASE_OPEN_SUPPORT_DOCUMENT_FAILURE,
  CASE_REQUEST_SCREEN_SUCCESS,
  CASE_REQUEST_SCREEN_FAILURE,
  DELETE_DIAGNOSIS_FAILURE,
  DELETE_DIAGNOSIS_SUCCESS,
  DELETE_DIAGNOSIS_REQUEST,
  CASE_UNDO_SCREEN_REQUEST,
  CASE_UNDO_SCREEN_SUCCESS,
  CASE_UNDO_SCREEN_FAILURE,
  REVIEWS_REQUEST,
  REVIEWS_SUCCESS,
  REVIEWS_FAILURE,
  CREATE_NOTE_REQUEST,
  CREATE_NOTE_SUCCESS,
  CREATE_NOTE_FAILURE,
  UPDATE_CASE_DOCUMENT_REQUEST,
  UPDATE_CASE_DOCUMENT_SUCCESS,
  UPDATE_CASE_DOCUMENT_FAILURE,
  COPATH_CASE_PATIENT_MATCHES_REQUEST,
  COPATH_CASE_PATIENT_MATCHES_SUCCESS,
  COPATH_CASE_PATIENT_MATCHES_FAILURE,
} from "./constants";
import {
  AsyncSagaAction,
  AsyncSagaActionNoArgs,
  createAsyncSaga,
} from "../utils/asyncActions";

export const caseDetail = (id: number) => ({
  type: CASE_DETAIL_REQUEST,
  payload: { id },
});

export const qaReviews = createAsyncSaga(
  REVIEWS_REQUEST,
  REVIEWS_SUCCESS,
  REVIEWS_FAILURE,
  (parentLookupCaseId: number) => `cases/${parentLookupCaseId}/reviews`,
  (parentLookupCaseId: number) => {
    return ApiService.listCaseQaReviews({ parentLookupCaseId });
  }
) as AsyncSagaAction<number>;

export const createNote = createAsyncSaga(
  CREATE_NOTE_REQUEST,
  CREATE_NOTE_SUCCESS,
  CREATE_NOTE_FAILURE,
  ({ parentLookupCaseId }) => `admin/cases/${parentLookupCaseId}/review`,
  ({ parentLookupCaseId, note }) => {
    return ApiService.createAdminCaseQaReview({
      parentLookupCaseId,
      requestBody: { reviewed: null, note },
    });
  }
) as AsyncSagaAction<number>;

export const wipeCaseDetail = () => ({
  type: WIPE_CASE_DETAIL,
});

export const caseLog = (caseId: number) => ({
  type: CASE_DETAIL_EVENTS_REQUEST,
  payload: { parentLookupCaseId: caseId },
});

export const patchCaseWorkflow = (payload: any) => ({
  type: CASE_PATCH_REQUEST,
  payload,
});

export const patchCaseStatus = (payload: any) => ({
  type: CASE_PATCH_STATUS_REQUEST,
  payload,
});

export const patchCaseAddon = (
  id: number,
  type: CaseDiagnosisAddonWritable.addon_type,
  content: string
) => ({
  type: UPDATE_CASE_ADDON_REQUEST,
  payload: { id, type, content },
});

type RequestScreenArgs = Parameters<typeof ApiService.requestScreenCase>[0];

export const requestScreen = createAsyncSaga(
  CASE_REQUEST_SCREEN_REQUEST,
  CASE_REQUEST_SCREEN_SUCCESS,
  CASE_REQUEST_SCREEN_FAILURE,
  ({ id }) => `cases/${id}/request_screen`,
  ApiService.requestScreenCase
) as AsyncSagaAction<RequestScreenArgs>;

export const undoScreen = createAsyncSaga(
  CASE_UNDO_SCREEN_REQUEST,
  CASE_UNDO_SCREEN_SUCCESS,
  CASE_UNDO_SCREEN_FAILURE,
  ({ id }) => `cases/${id}/cancel_screen`,
  ApiService.cancelScreenCase
) as AsyncSagaAction<{ id: number }>;

export const openDocument = (
  caseId: number,
  documentId: number,
  source?: string,
  user?: User,
  downloadFileName?: string
) => ({
  type: CASE_OPEN_DOCUMENT_REQUEST,
  payload: {
    parentLookupCaseId: caseId,
    id: documentId,
    source,
    user,
    downloadFileName,
  },
});

export const fetchCaseDiagnoses = (
  id: number,
  status?: string | Case.status,
  options = { ai_only: false }
) => ({
  type: CASE_DIAGNOSES_REQUEST,
  payload: {
    id,
    status,
    ai_only: options?.ai_only,
  },
});

export const createCaseDiagnosis = (caseDiagnosis: CaseDiagnosis) => ({
  type: DIAGNOSE_REQUEST,
  payload: { caseDiagnosis },
});

export const deleteCaseDiagnosis = createAsyncSaga(
  DELETE_DIAGNOSIS_REQUEST,
  DELETE_DIAGNOSIS_SUCCESS,
  DELETE_DIAGNOSIS_FAILURE,
  ({ id }) => `/diagnosis/${id}/delete`,
  ApiService.destroyCaseDiagnosis
) as AsyncSagaAction<{ id: number }>;

export const updateCaseDiagnosis = (
  id: number,
  caseDiagnosis: Partial<CaseDiagnosis>
) => ({
  type: UPDATE_DIAGNOSIS_REQUEST,
  payload: { id, caseDiagnosis },
});

export const initiateModifyReportProcedure = (
  caseId: number,
  source:
    | CaseDiagnosis.source.ADDEND
    | CaseDiagnosis.source.AMEND
    | CaseDiagnosis.source.CORRECTION
) => ({
  type: MODIFY_REPORT_REQUEST,
  payload: {
    case_id: caseId,
    source,
  },
});

export const cancelModifyReportProcedure = (caseId: number) => ({
  type: CANCEL_MODIFY_REPORT_REQUEST,
  payload: { case_id: caseId },
});

export const editScreenDiagnoses = (case_id: number) => ({
  type: EDIT_SCREEN_DIAGNOSES_REQUEST,
  payload: { case_id: case_id },
});

export const cancelEditScreenDiagnoses = (case_id: number) => ({
  type: CANCEL_EDIT_SCREEN_DIAGNOSES_REQUEST,
  payload: { case_id: case_id },
});

export const requestSignout = (
  id: number,
  navigate: NavigateFunction,
  signature_id?: number,
  report_type?: CaseReport.report_type,
  location_code_id?: number
) => ({
  type: CASE_SIGN_OUT_REQUEST,
  payload: {
    id,
    navigate,
    signature_id,
    report_type,
    location_code_id: location_code_id || undefined, // localStorage will store `0`
  },
});

export const recallReport = (id: number) => ({
  type: CASE_RECALL_REPORT_REQUEST,
  payload: id,
});

type StainOrderArgs = Parameters<typeof ApiService.createLabOrder>[0];

export const stainOrderRequest: AsyncSagaAction<StainOrderArgs> =
  createAsyncSaga(
    STAIN_ORDER_REQUEST,
    STAIN_ORDER_SUCCESS,
    STAIN_ORDER_FAILURE,
    ({ requestBody }) => `case/${requestBody.case_id}/create-stain-order`,
    ({ requestBody }: StainOrderArgs) =>
      ApiService.createLabOrder({ requestBody })
  );

type FetchSigneeArgs = Parameters<typeof ApiService.retrieveUser>[0];

export const fetchSignee: AsyncSagaAction<FetchSigneeArgs> = createAsyncSaga(
  REPORT_SIGNEE_REQUEST,
  REPORT_SIGNEE_SUCCESS,
  REPORT_SIGNEE_FAILURE,
  ({ id }: FetchSigneeArgs) => `signee-${id}`,
  ({ id }: FetchSigneeArgs) => ApiService.retrieveUser({ id })
);

type FetchPatientMatchesArgs = {
  case_id: number;
};
export const fetchPatientMatches: AsyncSagaAction<FetchPatientMatchesArgs> =
  createAsyncSaga(
    CASE_PATIENT_MATCHES_REQUEST,
    CASE_PATIENT_MATCHES_SUCCESS,
    CASE_PATIENT_MATCHES_FAILURE,
    ({ case_id }: FetchPatientMatchesArgs) => `patient-history-${case_id}`,
    ({ case_id }: FetchPatientMatchesArgs) =>
      ApiService.matchesCase({ id: case_id })
  ) as AsyncSagaActionNoArgs;

type updateSpecimenDetailsArgs = Parameters<
  typeof ApiService.partialUpdateSpecimen
>[0];

// https://github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js
// via https://stackoverflow.com/a/52171480
const cyrb53 = (str: string, seed = 0) => {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

type FetchCoPathPatientMatchesArgs = {
  case_id: number;
  patient?: Partial<Patient>;
};
export const fetchCopathPatientMatches: AsyncSagaAction<FetchCoPathPatientMatchesArgs> =
  createAsyncSaga(
    COPATH_CASE_PATIENT_MATCHES_REQUEST,
    COPATH_CASE_PATIENT_MATCHES_SUCCESS,
    COPATH_CASE_PATIENT_MATCHES_FAILURE,
    ({ case_id }: FetchPatientMatchesArgs) =>
      `copath-patient-history-${case_id}`,
    ({ case_id, patient }: FetchCoPathPatientMatchesArgs) => {
      const { first_name, last_name, date_of_birth, sex } = patient || {};
      return ApiService.copathMatchesCase({
        id: case_id,
        h: `${cyrb53(`${first_name}${last_name}${date_of_birth}${sex}`)}`,
      });
    }
  ) as AsyncSagaActionNoArgs;

export const updateSpecimenDetails = createAsyncSaga(
  UPDATE_CASE_SPECIMEN_REQUEST,
  UPDATE_CASE_SPECIMEN_SUCCESS,
  UPDATE_CASE_SPECIMEN_FAILURE,
  ({ id }: updateSpecimenDetailsArgs) => `update-specimen-details-${id}`,
  ApiService.partialUpdateSpecimen
) as AsyncSagaAction<updateSpecimenDetailsArgs>;

export const getSupportDocument = createAsyncSaga(
  CASE_OPEN_SUPPORT_DOCUMENT_REQUEST,
  CASE_OPEN_SUPPORT_DOCUMENT_SUCCESS,
  CASE_OPEN_SUPPORT_DOCUMENT_FAILURE,
  ({ documentType }: { documentType: UserSupportDocument.document_type }) =>
    `get-support-document-${documentType}`,
  ({ documentType }: { documentType: UserSupportDocument.document_type }) =>
    ApiService.retrieveUserSupportDocument({ documentType })
);

type updateCaseDocumentArgs = Parameters<
  typeof ApiService.partialUpdateCaseDocument
>[0];

export const updateCaseDocument = createAsyncSaga(
  UPDATE_CASE_DOCUMENT_REQUEST,
  UPDATE_CASE_DOCUMENT_SUCCESS,
  UPDATE_CASE_DOCUMENT_FAILURE,
  ({ parentLookupCaseId, id }) =>
    `cases/${parentLookupCaseId}/documents/${id}/`,
  ApiService.partialUpdateCaseDocument
) as AsyncSagaAction<updateCaseDocumentArgs>;
